import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPhone, FiMail, FiMapPin } from 'react-icons/fi';
import axios from 'axios';

const ContactContainer = styled.section`
  background: white;
  padding: 4rem 0;

  /* Small laptops and 125% zoom */
  @media (min-width: 1024px) and (max-width: 1366px) {
    padding: 3rem 0;
  }

  /* 13-15 inch laptops */
  @media (min-width: 1366px) and (max-width: 1600px) {
    padding: 3.5rem 0;
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }

  /* Extra small screens */
  @media (max-width: 480px) {
    padding: 1.5rem 0.5rem;
  }
`;

const ContactContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 0;
  }
`;

const ContactLeft = styled.div`
  h2 {
    color: ${props => props.theme.colors.heading};
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;

    @media (max-width: 768px) {
      font-size: 2rem;
      text-align: center;
    }
  }

  p {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 1.1rem;
    line-height: 1.7;
    margin-bottom: 2rem;

    @media (max-width: 768px) {
      font-size: 1rem;
      text-align: center;
    }
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${props => props.theme.colors.textSecondary};

  svg {
    color: ${props => props.theme.colors.primary};
    flex-shrink: 0;
    font-size: 1.25rem;
  }

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ContactLink = styled.a`
  color: ${props => props.theme.colors.textSecondary};
  text-decoration: none;
  transition: color 0.3s ease;
  cursor: pointer;

  &:hover {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
  }
`;

const ContactRight = styled.div`
  background: white;
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const FormTitle = styled.h3`
  color: ${props => props.theme.colors.heading};
  font-size: 1.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 0;
    width: 60px;
    height: 4px;
    background: ${props => props.theme.colors.primary};
    border-radius: 2px;
  }
`;

const ContactForm = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  grid-column: ${props => props.fullWidth ? '1 / -1' : 'auto'};
`;

const FormInput = styled.input`
  width: 100%;
  padding: 1rem 1.25rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  background-color: #f9f9f9;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(1, 103, 95, 0.1);
    outline: none;
    background-color: white;
  }
  
  &::placeholder {
    color: #aaa;
    font-weight: 400;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 1rem 1.25rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;
  background-color: #f9f9f9;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(1, 103, 95, 0.1);
    outline: none;
    background-color: white;
  }
  
  &::placeholder {
    color: #aaa;
    font-weight: 400;
  }
`;

const SubmitButton = styled.button`
  background-color: ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.primary};
  font-weight: 800;
  padding: 1rem 2rem;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  grid-column: 1 / -1;
  width: fit-content;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 10px rgba(143, 226, 0, 0.3);
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.secondaryDark};
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(143, 226, 0, 0.4);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background: ${props => props.theme.colors.success};
  color: white;
  padding: 1rem;
  border-radius: 6px;
  text-align: center;
  margin-top: 1rem;
`;

const ErrorMessage = styled.div`
  background: ${props => props.theme.colors.error};
  color: white;
  padding: 1rem;
  border-radius: 6px;
  text-align: center;
  margin-top: 1rem;
`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [siteSettings, setSiteSettings] = useState({
    contactInfo: {
      email: 'admin@rplsupport.com.au',
      phone: '+61 433 098 399',
      phoneSecondary: '+61 415 820 320',
      businessHours: 'Mon - Sat: 8:00 am - 7:00 pm'
    }
  });

  // Fetch site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/api/public/site-settings');
        if (response.data) {
          setSiteSettings(response.data);
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };
    
    fetchSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Simulate form submission
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Here you would typically send the data to your backend
      console.log('Form submitted:', formData);
      
      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactContainer>
      <ContactContent>
        <ContactLeft>
          <h2>Ready To Get Recognised?</h2>
          <p>
            Take the first step towards getting your skills recognized and advancing your career. 
            Our team is ready to help you navigate the RPL process and achieve your qualification goals.
          </p>
          
          <ContactInfo>
            <ContactItem>
              <FiPhone />
              <span>
                <ContactLink href={`tel:${siteSettings.contactInfo.phone}`}>
                  {siteSettings.contactInfo.phone}
                </ContactLink><br />
                <ContactLink href={`tel:${siteSettings.contactInfo.phoneSecondary}`}>
                  {siteSettings.contactInfo.phoneSecondary}
                </ContactLink>
              </span>
            </ContactItem>
            <ContactItem>
              <FiMail />
              <span>
                <ContactLink href={`mailto:${siteSettings.contactInfo.email}`}>
                  {siteSettings.contactInfo.email}
                </ContactLink>
              </span>
            </ContactItem>
            <ContactItem>
              <FiMapPin />
              <span>{siteSettings.contactInfo.businessHours}</span>
            </ContactItem>
          </ContactInfo>
        </ContactLeft>

        <ContactRight>
          <FormTitle>Book Your Free Consultation Today!</FormTitle>
          <ContactForm onSubmit={handleSubmit}>
            <FormGroup>
              <FormInput
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="First Name"
                required
              />
            </FormGroup>

            <FormGroup>
              <FormInput
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Last Name"
                required
              />
            </FormGroup>

            <FormGroup>
              <FormInput
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email Address"
                required
              />
            </FormGroup>

            <FormGroup>
              <FormInput
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Phone Number"
                required
              />
            </FormGroup>
            
            <FormGroup fullWidth>
              <FormTextarea
                name="message"
                placeholder="Type Your Message"
                value={formData.message || ''}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Send Message'}
            </SubmitButton>

            {submitStatus === 'success' && (
              <SuccessMessage>
                Thank you! We'll be in touch with you soon.
              </SuccessMessage>
            )}

            {submitStatus === 'error' && (
              <ErrorMessage>
                Something went wrong. Please try again.
              </ErrorMessage>
            )}
          </ContactForm>
        </ContactRight>
      </ContactContent>
    </ContactContainer>
  );
};

export default ContactSection; 